/**
 * Named tiles and sprites the renderer uses. Every entry is a packed {@link TileId}.
 * Indices were picked from the Kenney packs and verified on the contact sheet
 * (`docs/assets/contact-sheet.png`, regenerate with `python3 tools/assets/contact_sheet.py`).
 */
import type { TeamId, UnitType } from '@seven-dawns/shared';
import { SEVEN_DAWNS_TILES, type SevenDawnsTileName } from './generated';
import { SheetId, tileId, type TileId } from './sheets';

const town = (i: number): TileId => tileId(SheetId.town, i);
const dungeon = (i: number): TileId => tileId(SheetId.dungeon, i);
const farm = (i: number): TileId => tileId(SheetId.farm, i);
const desert = (i: number): TileId => tileId(SheetId.desert, i);
export const wb = (name: SevenDawnsTileName): TileId => tileId(SheetId.sevenDawns, SEVEN_DAWNS_TILES[name]);

/** Ground and decoration tiles, grouped by what the composer needs. */
export const TILES = {
  // --- temperate ground --------------------------------------------------------------------
  grass: town(0),
  grassTufts: town(1),
  grassFlowers: town(2),
  /** Plain dirt (roads, hill patches). Several variants to avoid repetition. */
  dirt: [town(39), town(40), town(41), town(42)],
  cobble: town(43),
  // --- vegetation (Tiny Town trees include a grass background, use on temperate only) ------
  treeGreen: [town(4), town(16), town(28)],
  treeAutumn: [town(3), town(15), town(27)],
  bush: town(5),
  sprout: town(17),
  mushrooms: town(29),
  pine: [farm(3), farm(15), farm(27)],
  shrubRound: farm(39),
  sunflower: farm(83),
  boulder: farm(89),
  pebbles: farm(77),
  deadTree: [farm(2), farm(14)],
  // --- farming (Tiny Farm) -----------------------------------------------------------------
  /** Tilled soil strip, left/mid/mid/right (horizontal rows). */
  soilRow: [farm(48), farm(49), farm(50), farm(51)],
  soilRowAlt: [farm(60), farm(61), farm(62), farm(63)],
  /** Ripe crops (transparent, drawn on soil). */
  crops: [farm(8), farm(20), farm(32), farm(44), farm(56), farm(68)],
  /** Young crops. */
  cropsYoung: [farm(6), farm(18), farm(30), farm(42), farm(54), farm(66)],
  hay: [farm(96), farm(97)],
  // --- arid ground (Desert Shooter Pack) ---------------------------------------------------
  sand: [desert(55), desert(73)],
  sandLight: [desert(64), desert(65)],
  sandRocks: desert(58),
  sandRockPile: desert(76),
  sandMound: desert(59),
  sandHole: [desert(60), desert(61)],
  bones: [desert(82), desert(83)],
  aridTree: wb('aridTree'),
  aridCactus: [wb('aridCactus'), wb('aridCactusSmall')],
  aridShrub: wb('aridShrub'),
  aridSpire: wb('aridSpire'),
  // --- hand-pixelled terrain (tools/assets/build_tiles.py) ---------------------------------
  hill: [wb('hill'), wb('hillSmall')],
  hillArid: [wb('hillArid'), wb('duneArid')],
  rockGround: [wb('rockGround0'), wb('rockGround1')],
  rockGroundArid: wb('rockGroundArid'),
  /** 2×2 mountain peak: TL, TR, BL, BR. */
  peak: [wb('peakTL'), wb('peakTR'), wb('peakBL'), wb('peakBR')],
  peakSmall: wb('peakSmall'),
  peakSmallArid: wb('peakSmallArid'),
  marshGround: wb('marshGround'),
  marshPuddle: [wb('marshPuddle0'), wb('marshPuddle1')],
  marshReeds: [wb('marshReeds0'), wb('marshReeds1')],
  bridgeNS: wb('bridgeNS'),
  bridgeEW: wb('bridgeEW'),
  // --- structures (Tiny Town) --------------------------------------------------------------
  signpost: town(83),
  fenceH: [town(80), town(81), town(82)],
  well: town(92),
  beehive: town(94),
  log: town(106),
} as const;

/** Water autotile by land mask (bit 1 = N, 2 = E, 4 = S, 8 = W is land). */
export function waterTile(mask: number): TileId {
  return tileId(SheetId.sevenDawns, SEVEN_DAWNS_TILES.water0 + (mask & 15));
}
/** Shallow ford water, same mask convention. */
export function shallowsTile(mask: number): TileId {
  return tileId(SheetId.sevenDawns, SEVEN_DAWNS_TILES.shallows0 + (mask & 15));
}
export const WATER_CORNERS = {
  NE: wb('waterCornerNE'),
  SE: wb('waterCornerSE'),
  SW: wb('waterCornerSW'),
  NW: wb('waterCornerNW'),
} as const;

/** A multi-tile structure: rows of tile ids (0 = leave empty), drawn top-left anchored. */
export type Stamp = readonly (readonly TileId[])[];

export const STAMPS = {
  /** 4×4 castle front: crenellations, windows, portcullis gate. */
  keep: [
    [town(99), town(100), town(100), town(101)],
    [town(125), town(126), town(126), town(125)],
    [town(126), town(111), town(112), town(126)],
    [town(126), town(123), town(124), town(126)],
  ],
  /** Red-roofed cottage, 3 wide. */
  houseRed: [
    [town(52), town(53), town(54)],
    [town(64), town(65), town(66)],
    [town(72), town(86), town(75)],
  ],
  /** Grey-roofed cottage, 3 wide. */
  houseGrey: [
    [town(48), town(49), town(50)],
    [town(60), town(61), town(62)],
    [town(76), town(90), town(79)],
  ],
  /** Small gabled hut, 1 wide. */
  hutRed: [[town(67)], [town(85)]],
  hutGrey: [[town(63)], [town(89)]],
  /** Stone watchtower, 1 wide × 3 tall. */
  watchtower: [[town(102)], [town(125)], [town(126)]],
  /** Shrine: statue on a cobbled plaza. */
  shrine: [[town(43), dungeon(7), town(43)]],
  /** Ley node: crystal above a glowing basin. */
  leyNode: [[wb('leyCrystal')], [dungeon(32)]],
  /** Big mountain peak (2×2). */
  peak: [
    [wb('peakTL'), wb('peakTR')],
    [wb('peakBL'), wb('peakBR')],
  ],
  /** Ruins: broken masonry and rubble. */
  ruins: [
    [town(102), 0, town(125)],
    [town(126), town(43), farm(77)],
  ],
} as const satisfies Record<string, Stamp>;

/** Map-unit sprites (Tiny Dungeon). Teams share sprites; team is shown by base shape + colour. */
export const UNIT_SPRITES: Record<UnitType, TileId> = {
  infantry: dungeon(96),
  archers: dungeon(112),
  cavalry: dungeon(97),
  healers: dungeon(99),
  scouts: dungeon(85),
};

export const SPRITES = {
  /** Fallback hero sprite (heroes normally use their Roguelike Characters composite). */
  hero: dungeon(87),
  wizard: dungeon(84),
  messenger: dungeon(88),
  raven: wb('raven'),
  scroll: wb('scroll'),
  relic: wb('relic'),
  leyCrystal: wb('leyCrystal'),
  sparks: wb('sparks'),
  swords: wb('swords'),
  chest: dungeon(89),
} as const;

/** Unit bases: shape differs by team so colour is never the only signal. */
export const BASES = {
  azure: wb('baseAzure'),
  ember: wb('baseEmber'),
  teal: wb('baseTeal'),
  gold: wb('baseGold'),
} as const;

export const BANNERS = {
  azure: wb('bannerAzure'),
  ember: wb('bannerEmber'),
  teal: wb('bannerTeal'),
  neutral: wb('bannerNeutral'),
} as const;

export function teamBanner(team: TeamId | null): TileId {
  return team === 0 ? BANNERS.azure : team === 1 ? BANNERS.ember : BANNERS.neutral;
}
export function teamBase(team: TeamId): TileId {
  return team === 0 ? BASES.azure : BASES.ember;
}

/** HUD / inline icons. */
export const ICONS = {
  food: farm(66),
  foodSack: farm(9),
  mana: dungeon(116),
  relic: wb('relic'),
  raven: wb('raven'),
  messenger: wb('scroll'),
  battle: wb('swords'),
  keep: town(97),
  village: town(67),
  shrine: dungeon(7),
  leyNode: wb('leyCrystal'),
  ruins: dungeon(57),
  watchtower: town(99),
} as const;
